import { svgImages } from "@/lib/svgImages";
import styles from "./pricingFeaturesGroup.module.scss";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import React from "react"; // Added missing import for React

interface Plan {
  plan_title: string;
  type_of_value: "text" | "boolean" | "format";
  boolean_value?: boolean;
  text_value?: string;
  format_value?: string[];
}

interface FeatureItem {
  feature_title: string;
  feature_key: string;
  feature_tooltip?: string;
  isStatic?: boolean;
  parent_feature?: string;
  plans: {
    plan: Plan[];
  }[];
}

interface PricingFeaturesGroupProps {
  mainTitle: string;
  groupItems: FeatureItem[];
}

const PricingFeaturesGroup = ({
  mainTitle,
  groupItems,
}: PricingFeaturesGroupProps) => {
  // Helper function to get display value based on feature and plan
  const getDisplayValue = (featureKey: string, plan: Plan) => {
    const planTitleLower = plan.plan_title.toLowerCase();

    // Handle Dynamic QR Codes for free plan
    if (featureKey === "dynamic_qr_codes" && planTitleLower === "free") {
      return (
        <div
          className={`${styles["booleanIcon"]} `}
          dangerouslySetInnerHTML={{
            __html: svgImages.qr_false,
          }}
        />
      );
    }

    // Handle Static QR codes - replace true icon with "Unlimited"
    if (
      featureKey === "static_qr_codes" &&
      plan.type_of_value === "boolean" &&
      plan.boolean_value === true
    ) {
      return <span className={`${styles["textValue"]}`}>Unlimited</span>;
    }

    // Handle Scans Data Analytics - add months after value
    if (
      (featureKey === "scans_data_analytics" ||
        featureKey === "clicks_data_analytics") &&
      plan.type_of_value === "text" &&
      plan.text_value
    ) {
      return (
        <span className={`${styles["textValue"]}`}>
          {plan.text_value} months
        </span>
      );
    }

    // Handle Scans - replace true icon with "Unlimited"
    if (
      featureKey === "scans" &&
      plan.type_of_value === "boolean" &&
      plan.boolean_value === true
    ) {
      return <span className={`${styles["textValue"]}`}>Unlimited</span>;
    }

    // Handle Team Members - change 0 to "Owner Only"
    if (
      featureKey === "team_members" &&
      plan.type_of_value === "text" &&
      plan.text_value === "0"
    ) {
      return <span className={`${styles["textValue"]}`}>Owner Only</span>;
    }

    // Handle Outlets - replace 0 with qr_false icon
    if (
      featureKey === "outlets" &&
      plan.type_of_value === "text" &&
      plan.text_value === "0"
    ) {
      return (
        <div
          className={`${styles["booleanIcon"]} `}
          dangerouslySetInnerHTML={{
            __html: svgImages.qr_false,
          }}
        />
      );
    }

    // Handle Download Format features - show only enabled formats
    if (
      featureKey.includes("download_format") &&
      plan.type_of_value === "boolean"
    ) {
      if (plan.boolean_value === true) {
        return (
          <div className={`${styles["formats"]}`}>
            <div className={`${styles["formatItem"]}`}>
              <span className={`${styles["formatName"]}`}>
                {featureKey.includes("png")
                  ? "PNG"
                  : featureKey.includes("jpg")
                  ? "JPG"
                  : featureKey.includes("svg")
                  ? "SVG"
                  : featureKey.includes("pdf")
                  ? "PDF"
                  : "Format"}
              </span>
            </div>
          </div>
        );
      }
      return null; // Don't show disabled formats
    }

    // Default handling for other cases
    if (plan.type_of_value === "text") {
      return (
        <span className={`${styles["textValue"]}`}>{plan.text_value}</span>
      );
    } else if (plan.type_of_value === "boolean") {
      return (
        <>
          {plan.boolean_value === true ? (
            <div
              className={`${styles["booleanIcon"]} `}
              dangerouslySetInnerHTML={{
                __html: svgImages.qr_true,
              }}
            />
          ) : (
            <div
              className={`${styles["booleanIcon"]} `}
              dangerouslySetInnerHTML={{
                __html: svgImages.qr_false,
              }}
            />
          )}
        </>
      );
    } else if (plan.type_of_value === "format") {
      return (
        <div className={`${styles["formats"]}`}>
          {plan?.format_value?.map((type, typeIndex) => (
            <div
              className={`${styles["formatItem"]}`}
              key={`${type}-${typeIndex}`}
            >
              {type}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // Group items to handle download_format specially
  const groupedItems = groupItems.reduce((acc, item) => {
    if (item.parent_feature === "download_format") {
      if (!acc.downloadFormat) {
        acc.downloadFormat = [];
      }
      acc.downloadFormat.push(item);
    } else {
      if (!acc.other) {
        acc.other = [];
      }
      acc.other.push(item);
    }
    return acc;
  }, {} as { downloadFormat?: FeatureItem[]; other?: FeatureItem[] });

  // Find the index of the Scans feature to insert download format after it
  const scansIndex =
    groupedItems.other?.findIndex((item) => item.feature_key === "scans") ?? -1;

  return (
    <div className={`${styles["pricingFeaturesGroup"]}`}>
      <div className={`${styles["title"]}`}>
        <h3 className={`${styles["titleValue"]}`}>{mainTitle}</h3>
        <div className={`${styles["titleValue"]}`}></div>
        <div className={`${styles["titleValue"]}`}></div>
        <div className={`${styles["titleValue"]} ${styles["highlight"]}`}></div>
        <div className={`${styles["titleValue"]}`}></div>
      </div>
      <ul>
        {/* Render features with download format inserted after Scans */}
        {groupedItems.other &&
          groupedItems.other.map((item, index) => {
            const shouldInsertDownloadFormat =
              index === scansIndex &&
              groupedItems.downloadFormat &&
              groupedItems.downloadFormat.length > 0;

            return (
              <React.Fragment key={`${item.feature_title}-${index}`}>
                <li className={`${styles["feature"]}`}>
                  <div className={`${styles["featureTitle"]}`}>
                    <span>{item.feature_title}</span>

                    {item.isStatic && (
                      <span className={`${styles["staticBadge"]}`}>Static</span>
                    )}
                    {item.feature_tooltip ? (
                      <>
                        <span
                          data-tooltip-id={`featureTooltip-${index}`}
                          data-tooltip-content={item.feature_tooltip}
                          dangerouslySetInnerHTML={{
                            __html: svgImages.infoIcon,
                          }}
                        />

                        <Tooltip
                          id={`featureTooltip-${index}`}
                          place="right"
                          className={`${styles["featureTooltip"]}`}
                        />
                      </>
                    ) : (
                      ""
                    )}
                  </div>

                  {item.plans[0]?.plan &&
                    item.plans[0].plan.map((plan, planIndex) => (
                      <div
                        key={`${plan.plan_title}-${planIndex}`}
                        className={`${styles["featureValueContainer"]} ${
                          plan.plan_title === "Plus" ? styles["highlight"] : ""
                        }`}
                      >
                        <div className={`${styles["planName"]}`}>
                          {plan.plan_title}
                        </div>

                        <div className={`${styles["featureValue"]} `}>
                          {getDisplayValue(item.feature_key, plan)}
                        </div>
                      </div>
                    ))}
                </li>

                {/* Insert download format section after Scans */}
                {shouldInsertDownloadFormat && (
                  <li className={`${styles["feature"]}`}>
                    <div className={`${styles["featureTitle"]}`}>
                      <span className={`${styles["downloadFormatBadge"]}`}>
                        Download Format
                      </span>
                    </div>

                    {/* Get all plans from the first download format item */}
                    {groupedItems.downloadFormat[0].plans[0]?.plan &&
                      groupedItems.downloadFormat[0].plans[0].plan.map(
                        (plan, planIndex) => (
                          <div
                            key={`${plan.plan_title}-${planIndex}`}
                            className={`${styles["featureValueContainer"]} ${
                              plan.plan_title === "Plus"
                                ? styles["highlight"]
                                : ""
                            }`}
                          >
                            <div className={`${styles["planName"]}`}>
                              {plan.plan_title}
                            </div>

                            <div className={`${styles["featureValue"]} `}>
                              <div className={`${styles["formats"]}`}>
                                {groupedItems.downloadFormat.map(
                                  (formatItem, formatIndex) => {
                                    const formatPlan =
                                      formatItem.plans[0]?.plan?.find(
                                        (p) => p.plan_title === plan.plan_title
                                      );
                                    if (
                                      formatPlan &&
                                      formatPlan.type_of_value === "boolean" &&
                                      formatPlan.boolean_value === true
                                    ) {
                                      return (
                                        <div
                                          className={`${styles["formatItem"]}`}
                                          key={`${formatItem.feature_key}-${formatIndex}`}
                                        >
                                          <span
                                            className={`${styles["formatName"]}`}
                                          >
                                            {formatItem.feature_title}
                                          </span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      )}
                  </li>
                )}
              </React.Fragment>
            );
          })}
      </ul>
    </div>
  );
};

export default PricingFeaturesGroup;
